import { InputHTMLAttributes, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import EyeIcon from '@app/assets/icons/eye.png';
import EyeClosedIcon from '@app/assets/icons/eye-closed.svg?react';
import clsx from 'clsx';
import { debounce } from 'lodash';

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  containerClass?: string;
  isOptional?: boolean;
  externalHandler?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hideErrors?: boolean;
}

export const InputField = ({
  name,
  label,
  containerClass,
  isOptional,
  externalHandler,
  hideErrors,
  ...rest
}: InputFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    trigger,
    // @ts-expect-error this are need to update the input state on errors/etc
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    formState: { errors, isDirty },
  } = useFormContext();
  const field = register(name ?? '');
  const debouncedTrigger = useMemo(
    () =>
      debounce((fieldName: string) => {
        void trigger(fieldName);
      }, 500),
    [trigger]
  );

  useEffect(() => () => debouncedTrigger.cancel(), [debouncedTrigger]);

  return (
    <div className={`w-full ${containerClass ?? ''}`}>
      {label && (
        <label htmlFor={name} className="font-medium text-sm inline-block mb-1">
          {label} {isOptional && <span className="text-base-coolGray">(Optional)</span>}
        </label>
      )}
      <div className="relative">
        <input
          {...rest}
          type={
            rest.type === 'password'
              ? showPassword
                ? 'text'
                : 'password'
              : rest.type
          }
          {...field}
          onChange={(e) => {
            field.onChange(e);

            if (externalHandler) {
              externalHandler(e);
            }

            // trigger validation only after typing stops
            if (name) debouncedTrigger(name);
          }}
          className={clsx(
            `bg-base-seasalt rounded py-3 px-4 pr-12 w-full outline-brand-baseBlue outline-1 ${rest.className ?? ''}`,
            { 'border border-system-error': !hideErrors && name && errors[name] }
          )}
        />
        {rest.type === 'password' && (
          <button
            type="button"
            className="absolute inset-y-0 right-3 flex items-center text-base-coolGray hover:text-base-davyGray transition-colors"
            onClick={() => {
              setShowPassword(!showPassword);
            }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {!showPassword ? (
              <EyeClosedIcon className="w-7 h-7" />
            ) : (
              <img src={EyeIcon} alt="Show password" className="w-6.2 h-5" />
            )}
          </button>
        )}
      </div>
      {!hideErrors && name && errors[name] && (
        <p role="alert" className="text-system-error text-sm text-left pt-1">
          {typeof errors?.[name]?.message === 'string'
            ? errors[name]?.message
            : ''}
        </p>
      )}
    </div>
  );
};
